/**
 * FlahaFast Unified API Service
 * 
 * Single API service that replaces all v1/v2 confusion
 * All endpoints use the unified API structure /api/*
 * 
 * @version 2.0.0
 * @date January 6, 2025
 */

import axios from 'axios';
import { apiEndpoints, defaultAxiosConfig } from '../config/api';

// Types
import { WaterQualityData } from '../types/waterQuality';
import { Formulation, FormulationSummary } from '../types/formulation';

// Configure axios with default settings
const api = axios.create(defaultAxiosConfig);

// Attach Bearer token for authenticated API calls
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// =============================================================================
// HEALTH & SYSTEM
// =============================================================================

export const getHealth = async () => {
  try {
    const response = await api.get(apiEndpoints.health);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Health check failed: ${errorMessage}`);
  }
};

// =============================================================================
// AUTHENTICATION
// =============================================================================

export const login = async (email: string, password: string) => {
  try {
    const response = await api.post(apiEndpoints.auth.login, { email, password });
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Login failed: ${errorMessage}`);
  }
};

export const register = async (userData: any) => {
  try {
    const response = await api.post(apiEndpoints.auth.register, userData);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Registration failed: ${errorMessage}`);
  }
};

export const logout = async () => {
  try {
    const response = await api.post(apiEndpoints.auth.logout);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Logout failed: ${errorMessage}`);
  }
};

export const getProfile = async () => {
  try {
    const response = await api.get(apiEndpoints.auth.profile);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to get profile: ${errorMessage}`);
  }
};

// =============================================================================
// ELEMENTS
// =============================================================================

export const getElements = async () => {
  try {
    const response = await api.get(apiEndpoints.elements.list);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching elements: ${errorMessage}`);
  }
};

export const searchElements = async (searchTerm: string) => {
  try {
    const response = await api.get(apiEndpoints.elements.search, {
      params: { q: searchTerm }
    });
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error searching elements: ${errorMessage}`);
  }
};

export const getElementById = async (id: string) => {
  try {
    const response = await api.get(apiEndpoints.elements.byId(id));
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching element: ${errorMessage}`);
  }
};

// =============================================================================
// SALTS
// =============================================================================

export const getSalts = async () => {
  try {
    const response = await api.get(apiEndpoints.salts.list);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching salts: ${errorMessage}`);
  }
};

export const searchSalts = async (searchTerm: string) => {
  try {
    const response = await api.get(apiEndpoints.salts.search, {
      params: { q: searchTerm }
    });
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error searching salts: ${errorMessage}`);
  }
};

export const getCustomSalts = async () => {
  try {
    const response = await api.get(apiEndpoints.salts.custom);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching custom salts: ${errorMessage}`);
  }
};

export const getSaltById = async (id: string) => {
  try {
    const response = await api.get(apiEndpoints.salts.byId(id));
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching salt: ${errorMessage}`);
  }
};

// =============================================================================
// WATER PROFILES
// =============================================================================

export const getWaterProfiles = async () => {
  try {
    const response = await api.get(apiEndpoints.waterProfiles.list);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching water profiles: ${errorMessage}`);
  }
};

export const searchWaterProfiles = async (searchTerm: string) => {
  try {
    const response = await api.get(apiEndpoints.waterProfiles.search, {
      params: { q: searchTerm }
    });
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error searching water profiles: ${errorMessage}`);
  }
};

export const getWaterProfileById = async (id: string) => {
  try {
    const response = await api.get(apiEndpoints.waterProfiles.byId(id));
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching water profile: ${errorMessage}`);
  }
};

// =============================================================================
// WATER QUALITY (Enhanced)
// =============================================================================

export const getWaterQuality = async () => {
  try {
    const response = await api.get(apiEndpoints.waterQuality.list);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching water quality: ${errorMessage}`);
  }
};

export const analyzeWaterQuality = async (waterParams: Partial<WaterQualityData>, cropType?: string) => {
  try {
    const response = await api.post(apiEndpoints.waterQuality.analyze, {
      waterParams,
      cropType
    });
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error analyzing water quality: ${errorMessage}`);
  }
};

export const getOptimalParameters = async (cropType: string) => {
  try {
    const response = await api.get(apiEndpoints.waterQuality.optimalParameters(cropType));
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching optimal parameters: ${errorMessage}`);
  }
};

export const getWaterQualityById = async (id: string) => {
  try {
    const response = await api.get(apiEndpoints.waterQuality.byId(id));
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching water quality profile: ${errorMessage}`);
  }
};

// =============================================================================
// FORMULATIONS (Enhanced)
// =============================================================================

export const getFormulations = async () => {
  try {
    const response = await api.get(apiEndpoints.formulations.list);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching formulations: ${errorMessage}`);
  }
};

export const searchFormulations = async (searchTerm: string) => {
  try {
    const response = await api.get(apiEndpoints.formulations.search, {
      params: { q: searchTerm }
    });
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error searching formulations: ${errorMessage}`);
  }
};

export const getFormulationById = async (id: string) => {
  try {
    const response = await api.get(apiEndpoints.formulations.byId(id));
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching formulation: ${errorMessage}`);
  }
};

export const createFormulation = async (formulation: Omit<Formulation, 'id'>) => {
  try {
    const response = await api.post(apiEndpoints.formulations.create, formulation);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error creating formulation: ${errorMessage}`);
  }
};

export const updateFormulation = async (id: string, formulation: Partial<Formulation>) => {
  try {
    const response = await api.put(apiEndpoints.formulations.update(id), formulation);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error updating formulation: ${errorMessage}`);
  }
};

export const deleteFormulation = async (id: string) => {
  try {
    await api.delete(apiEndpoints.formulations.delete(id));
    return { id };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error deleting formulation: ${errorMessage}`);
  }
};

// =============================================================================
// CALCULATIONS (Comprehensive)
// =============================================================================

export const calculateStockSolution = async (data: Record<string, unknown>) => {
  try {
    const response = await api.post(apiEndpoints.calculations.stockSolution, data);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error calculating stock solution: ${errorMessage}`);
  }
};

export const calculateStoichiometry = async (data: Record<string, unknown>) => {
  try {
    const response = await api.post(apiEndpoints.calculations.stoichiometry, data);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error calculating stoichiometry: ${errorMessage}`);
  }
};

export const calculateBatch = async (data: Record<string, unknown>) => {
  try {
    const response = await api.post(apiEndpoints.calculations.batch, data);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error calculating batch: ${errorMessage}`);
  }
};

export const optimizeStockSolutions = async (data: Record<string, unknown>) => {
  try {
    const response = await api.post(apiEndpoints.calculations.optimizeStockSolutions, data);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error optimizing stock solutions: ${errorMessage}`);
  }
};

// =============================================================================
// pH ADJUSTMENT
// =============================================================================

export const getPhProducts = async () => {
  try {
    const response = await api.get(apiEndpoints.ph.products);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching pH products: ${errorMessage}`);
  }
};

export const calculatePhAdjustment = async (data: Record<string, unknown>) => {
  try {
    const response = await api.post(apiEndpoints.ph.calculate, data);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error calculating pH adjustment: ${errorMessage}`);
  }
};

export const predictPh = async (data: Record<string, unknown>) => {
  try {
    const response = await api.post(apiEndpoints.ph.predict, data);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error predicting pH: ${errorMessage}`);
  }
};

export const getPhRecommendations = async (data: Record<string, unknown>) => {
  try {
    const response = await api.post(apiEndpoints.ph.recommendations, data);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error getting pH recommendations: ${errorMessage}`);
  }
};

// =============================================================================
// CROP RECOMMENDATIONS
// =============================================================================

export const getCropTypes = async () => {
  try {
    const response = await api.get(apiEndpoints.crops.types);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error fetching crop types: ${errorMessage}`);
  }
};

export const generateCropRecommendations = async (data: Record<string, unknown>) => {
  try {
    const response = await api.post(apiEndpoints.crops.recommendations, data);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error generating crop recommendations: ${errorMessage}`);
  }
};

export const compareCropProfiles = async (data: Record<string, unknown>) => {
  try {
    const response = await api.post(apiEndpoints.crops.compare, data);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Error comparing crop profiles: ${errorMessage}`);
  }
};

// =============================================================================
// LEGACY ALIASES (for backward compatibility)
// =============================================================================

// Alias old function names to new unified functions
export const calculateFormulation = calculateStockSolution; // Legacy alias
export const getWaterQualityV2 = getWaterQuality; // Legacy alias
export const analyzeWaterQualityV2 = analyzeWaterQuality; // Legacy alias
export const getSaltsV2 = getSalts; // Legacy alias
export const getFormulationsV2 = getFormulations; // Legacy alias
